import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Card, CardBody, Col, Container, Row, Button, Form,
  Label, Input, Spinner, FormFeedback, Alert, InputGroup
} from 'reactstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { saveStdInfo as onSaveStdInfo } from '../../../slices/exam/thunk';
import { questionListResetFlag, questionSaveResetFlage } from '../../../slices/exam/thunk';
import {
  getRiderRegistrationData,
  riderRegister,
  riderCheckStudent,
  studentCheckStudent
} from '../../../helpers/custom_helper/backend_helper';
import { api } from '../../../config';
import ParticlesAuth from "../../../pages/AuthenticationInner/ParticlesAuth";
import { Link } from "react-router-dom";
import logoLight from "../../../assets/images/logo-light.png";

const RiderRegistration = ({ mode = 'student' }) => {
  const isRiderMode = mode === 'rider';
  const registrationScope = isRiderMode ? 'rider' : 'student';
  const registrationEntryPath = isRiderMode ? '/rider-register' : '/student-register';
  const defaultCourseCode = isRiderMode ? 'MCD' : '';

  document.title = isRiderMode
    ? "MC Delivery Rider Registration | Theory Practice"
    : "Student Registration | Theory Practice";

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mediaSource = api.IMAGE_URL;

  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [storedData, setStoredData] = useState(null);
  const [registrationError, setRegistrationError] = useState("");
  const [isExistingStudent, setIsExistingStudent] = useState(false);
  const [lockedFields, setLockedFields] = useState([]);
  const [canRegister, setCanRegister] = useState(true);
  const [blockReason, setBlockReason] = useState("");
  const [reattemptApproved, setReattemptApproved] = useState(false);
  const [showRegistrationDetails, setShowRegistrationDetails] = useState(false);

  const [formData, setFormData] = useState({
    std_name: '',
    email: '',
    traffic_id: '',
    mobile_no: '',
    course_code: defaultCourseCode,
    nationality: '',
    all_data: null,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const dataFromLocalStorage = localStorage.getItem('apiData');
    setStoredData(dataFromLocalStorage ? JSON.parse(dataFromLocalStorage) : null);

    dispatch(questionSaveResetFlage());
    dispatch(questionListResetFlag());
    fetchRegistrationData();
  }, []);

  const fetchRegistrationData = async () => {
    try {
      const response = await getRiderRegistrationData(registrationScope);
      if (response && response.success) {
        setCourses(response.data.courses || []);
      }
    } catch (error) {
      console.error('Error loading registration data', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setRegistrationError("");

    if (name === 'traffic_id') {
      setIsExistingStudent(false);
      setLockedFields([]);
      setCanRegister(true);
      setBlockReason("");
      setReattemptApproved(false);
      setShowRegistrationDetails(false);
    }
  };

  const applyStudentData = (studentData) => {
    const newLockedFields = [];
    if (studentData.std_name && studentData.std_name.trim() !== "") newLockedFields.push('std_name');
    if (studentData.email && studentData.email.trim() !== "") newLockedFields.push('email');
    if (studentData.mobile_no && studentData.mobile_no.toString().trim() !== "") newLockedFields.push('mobile_no');
    if (studentData.nationality && studentData.nationality.trim() !== "") newLockedFields.push('nationality');
    if (!isRiderMode && studentData.course_code && studentData.course_code.trim() !== "") {
      newLockedFields.push('course_code');
    }

    setLockedFields(newLockedFields);

    setFormData(prev => ({
      ...prev,
      std_name: studentData.std_name || prev.std_name,
      email: studentData.email || prev.email,
      mobile_no: studentData.mobile_no || prev.mobile_no,
      nationality: (studentData.nationality && studentData.nationality.trim() !== "") ? studentData.nationality : "",
      course_code: studentData.course_code || defaultCourseCode,
      all_data: studentData.all_data || null,
    }));

    const canRegisterValue = studentData.can_register !== false;
    const isReattemptApproved = studentData.exam_reattempt_approved === 1 || studentData.exam_reattempt_approved === '1';
    setCanRegister(canRegisterValue);
    setBlockReason(studentData.block_reason || "");
    setReattemptApproved(isReattemptApproved);
    setIsExistingStudent(true);
    setShowRegistrationDetails(true);

    if (!canRegisterValue) {
      setRegistrationError("");
    } else if (isReattemptApproved) {
      toast.info("Reattempt approved — you may start a new exam.");
    } else {
      toast.info(isRiderMode ? "Student found." : "Student details retrieved successfully.");
    }
  };

  const resetStudentForm = () => {
    setIsExistingStudent(false);
    setLockedFields([]);
    setCanRegister(true);
    setBlockReason("");
    setReattemptApproved(false);
    setFormData(prev => ({
      ...prev,
      std_name: '',
      email: '',
      mobile_no: '',
      nationality: '',
      course_code: defaultCourseCode,
      all_data: null,
    }));
  };

  const handleCheckStudent = async () => {
    if (!formData.traffic_id.trim()) {
      setErrors(prev => ({ ...prev, traffic_id: 'Enter Traffic ID first' }));
      return;
    }

    setIsChecking(true);
    setRegistrationError("");
    setCanRegister(true);
    setBlockReason("");
    setReattemptApproved(false);
    try {
      const checkFn = isRiderMode ? riderCheckStudent : studentCheckStudent;
      const response = await checkFn({ traffic_id: formData.traffic_id });
      if (response && response.success) {
        applyStudentData(response.data);
      } else {
        resetStudentForm();
        const message = response?.message || (isRiderMode
          ? "Student not found. Please register as a new user."
          : "Student not found. Please contact the center.");
        if (isRiderMode) {
          setShowRegistrationDetails(true);
          toast.warning(message);
        } else {
          setShowRegistrationDetails(false);
          setRegistrationError(message);
          toast.error(message);
        }
      }
    } catch (error) {
      console.error("Error checking student", error);
      toast.error("Unable to check student. Please try again.");
    } finally {
      setIsChecking(false);
    }
  };

  const isFieldLocked = (fieldName) => {
    return isExistingStudent && lockedFields.includes(fieldName);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.std_name.trim()) newErrors.std_name = 'Please enter your full name';
    if (!formData.email.trim()) newErrors.email = 'Please enter your email';
    if (!formData.traffic_id.trim()) newErrors.traffic_id = 'Please enter traffic file number';
    if (!formData.mobile_no.trim()) newErrors.mobile_no = 'Please enter mobile number';
    if (isRiderMode && !formData.nationality.trim()) newErrors.nationality = 'Please enter nationality';
    if (!formData.course_code) newErrors.course_code = 'Please select a course';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!canRegister) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await riderRegister({
        ...formData,
        registration_scope: registrationScope,
      });

      if (response && response.success) {
        const examData = response.data;
        window.localStorage.setItem("StudentInformation", JSON.stringify(examData));
        window.localStorage.setItem('systemLocalIp', 'rider-self-register');
        window.localStorage.setItem('registrationEntryPath', registrationEntryPath);
        dispatch(onSaveStdInfo(examData));

        toast.success(response.message || (isExistingStudent ? 'Exam Initialized!' : 'Registration Successful!'), {
          position: "top-right",
          autoClose: 1000,
        });

        setTimeout(() => {
          navigate('/rider-exam-start');
        }, 1000);
      } else {
        setRegistrationError(response?.message || 'Action failed. Please try again.');
      }
    } catch (error) {
      console.log(error);
      setRegistrationError(error?.message || 'Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageHeading = isRiderMode
    ? (isExistingStudent ? "Welcome Back!" : "MC Delivery Rider Registration")
    : (isExistingStudent ? "Welcome Back!" : "Student Registration");

  const pageSubtext = isExistingStudent && !canRegister
    ? "You have already passed the exam. Contact the center or wait for reattempt approval."
    : isExistingStudent && reattemptApproved
    ? "Reattempt approved — review your details to start a new exam."
    : isExistingStudent
    ? "Review your details to start a new exam attempt."
    : showRegistrationDetails
    ? (isRiderMode
      ? "Student not found. Please complete the form to register."
      : "Enter your traffic file number and click Check to retrieve your details.")
    : (isRiderMode
      ? "Register to proceed to your theory exam. Already registered? Use the check button."
      : "Enter your traffic file number to retrieve your details from Belhasa Driving Center.");

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-block auth-logo">
                      <img src={storedData && storedData.logo ? mediaSource + storedData.logo : logoLight} alt="" height="75" />
                    </Link>
                  </div>
                  <p className="mt-3 fs-15 fw-medium">{storedData ? storedData.title : "Theory Practice"}</p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={10} lg={8} xl={7}>
                <Card className="mt-4">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">{pageHeading}</h5>
                      <p className="text-muted">{pageSubtext}</p>
                    </div>

                    {showRegistrationDetails && (
                      <>
                        {isExistingStudent && !canRegister && blockReason && (
                          <Alert color="warning">
                            {blockReason}
                          </Alert>
                        )}

                        {isExistingStudent && canRegister && reattemptApproved && (
                          <Alert color="info">
                            Reattempt approved — you may start a new exam.
                          </Alert>
                        )}

                        {registrationError && canRegister && (
                          <Alert color="danger">
                            {registrationError}
                          </Alert>
                        )}
                      </>
                    )}

                    {!showRegistrationDetails && registrationError && (
                      <Alert color="danger">
                        {registrationError}
                      </Alert>
                    )}

                    <div className="p-2 mt-4">
                      <Form onSubmit={handleSubmit} className="needs-validation">

                        <Row>
                          <Col md={12}>
                            <div className="mb-3">
                              <Label htmlFor="traffic_id" className="form-label">Traffic File No <span className="text-danger">*</span></Label>
                              <InputGroup>
                                <Input
                                  type="text"
                                  name="traffic_id"
                                  id="traffic_id"
                                  value={formData.traffic_id}
                                  onChange={handleChange}
                                  placeholder="Enter traffic file no"
                                  invalid={!!errors.traffic_id}
                                />
                                <Button color="primary" type="button" onClick={handleCheckStudent} disabled={isChecking}>
                                  {isChecking ? <Spinner size="sm" /> : "Check"}
                                </Button>
                                {errors.traffic_id && <FormFeedback>{errors.traffic_id}</FormFeedback>}
                              </InputGroup>
                              <small className="text-muted">
                                {isRiderMode
                                  ? "Registered already? Enter ID and click Check to autofill."
                                  : "Enter your traffic file number and click Check to retrieve your details."}
                              </small>
                            </div>
                          </Col>
                        </Row>

                        {showRegistrationDetails && (
                        <>
                        <Row>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label htmlFor="std_name" className="form-label">Full Name <span className="text-danger">*</span></Label>
                              <Input
                                type="text"
                                name="std_name"
                                id="std_name"
                                value={formData.std_name}
                                onChange={handleChange}
                                placeholder="Enter full name"
                                invalid={!!errors.std_name}
                                readOnly={isFieldLocked('std_name')}
                                className={isFieldLocked('std_name') ? "bg-light text-muted" : ""}
                              />
                              {errors.std_name && <FormFeedback>{errors.std_name}</FormFeedback>}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label htmlFor="email" className="form-label">Email Address <span className="text-danger">*</span></Label>
                              <Input
                                type="email"
                                name="email"
                                id="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter email address"
                                invalid={!!errors.email}
                                readOnly={isFieldLocked('email')}
                                className={isFieldLocked('email') ? "bg-light text-muted" : ""}
                              />
                              {errors.email && <FormFeedback>{errors.email}</FormFeedback>}
                            </div>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label htmlFor="mobile_no" className="form-label">Mobile Number <span className="text-danger">*</span></Label>
                              <Input
                                type="text"
                                name="mobile_no"
                                id="mobile_no"
                                value={formData.mobile_no}
                                onChange={handleChange}
                                placeholder="Enter mobile number"
                                invalid={!!errors.mobile_no}
                                readOnly={isFieldLocked('mobile_no')}
                                className={isFieldLocked('mobile_no') ? "bg-light text-muted" : ""}
                              />
                              {errors.mobile_no && <FormFeedback>{errors.mobile_no}</FormFeedback>}
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className='mb-3'>
                              <Label htmlFor='nationality' className="form-label">
                                Nationality {isRiderMode && <span className="text-danger">*</span>}
                              </Label>
                              <Input
                                type="text"
                                name="nationality"
                                id="nationality"
                                value={formData.nationality}
                                onChange={handleChange}
                                placeholder="Enter nationality"
                                invalid={!!errors.nationality}
                                readOnly={isFieldLocked('nationality')}
                                className={isFieldLocked('nationality') ? "bg-light text-muted" : ""}
                              />
                              {errors.nationality && <FormFeedback>{errors.nationality}</FormFeedback>}
                            </div>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={12}>
                            <div className="mb-3">
                              <Label htmlFor="course_code" className="form-label">Course <span className="text-danger">*</span></Label>
                              <Input
                                type="select"
                                name="course_code"
                                id="course_code"
                                value={formData.course_code || defaultCourseCode}
                                onChange={handleChange}
                                invalid={!!errors.course_code}
                                disabled
                              >
                                <option value="">Select Course</option>
                                {courses.map((course) => (
                                  <option key={course.id} value={course.short_name}>
                                    {course.full_name}
                                  </option>
                                ))}
                              </Input>
                              {errors.course_code && <FormFeedback>{errors.course_code}</FormFeedback>}
                            </div>
                          </Col>
                        </Row>

                        <div className="mt-4">
                          <Button color="success" className="btn btn-success w-100" type="submit" disabled={isSubmitting || !canRegister || isLoading}>
                            {isSubmitting ? <Spinner size="sm" className='me-2'> Loading... </Spinner> : null}
                            {isExistingStudent ? "Start Exam Attempt" : "Register & Start Exam"}
                          </Button>
                        </div>
                        </>
                        )}
                      </Form>
                    </div>
                  </CardBody>
                </Card>
                <div className="mt-4 text-center">
                  <p className="mb-0 text-50">Need help? Contact system administrator</p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
      <ToastContainer autoClose={2000} limit={1} />
    </React.Fragment>
  );
}

export default RiderRegistration;
